import { i18n } from '@/i18n';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { approvalsApi } from '@/api/approvals.api';
import { hrApi } from '@/api';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useApprovalAccess } from '@/hooks/useApprovalAccess';
import { useWorkflowActions } from '@/hooks/useWorkflowActions';
import { useCurrentEmployeeId } from '@/modules/hr/hooks/useCurrentEmployeeId';
import { isLeaveApprovalReturned, isLeaveOwnerUser } from '@/modules/approvals/utils/returnedApprovalUtils';
import { invalidate, tierOptions } from '@/lib/query';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

export interface UseEntityApprovalActionsOptions {
  ownerModule: string;
  entityType: string;
  entityId: number | null | undefined;
  essScope?: boolean;
  allowRequesterView?: boolean;
  /** Leave employees.id — enables owner detection when HR submitted on behalf of the employee. */
  subjectEmployeeId?: number | null;
  /** Entity record status (leave statusCode or attendance approvalStatus). */
  recordStatusCode?: string | null;
  /** @deprecated Use recordStatusCode */
  leaveStatusCode?: string | null;
  onActionComplete?: (action?: string) => void;
}

export function useEntityApprovalActions({
  ownerModule,
  entityType,
  entityId,
  essScope = false,
  allowRequesterView = false,
  subjectEmployeeId,
  recordStatusCode,
  leaveStatusCode,
  onActionComplete,
}: UseEntityApprovalActionsOptions) {
  const resolvedRecordStatus = recordStatusCode ?? leaveStatusCode;
  const qc = useQueryClient();
  const { canViewPanel, canViewInbox } = useApprovalAccess();
  const currentUserId = useAppSelector((s) => s.auth.user?.userId);
  const currentEmployeeId = useCurrentEmployeeId();
  const canUseApprovalLookup = essScope || canViewPanel || canViewInbox;
  const enabled = entityId != null && entityId > 0 && currentUserId != null;
  const [comments, setComments] = useState('');
  const [acting, setActing] = useState(false);

  const lookupInstance = async () => {
    try {
      if (essScope && entityType === 'LEAVE') {
        return (await hrApi.essLeaveApproval(entityId!)) ?? null;
      }
      if (essScope && entityType === 'LEAVE_CANCEL') {
        return (await hrApi.essLeaveCancelApproval(entityId!)) ?? null;
      }
      return (await approvalsApi.lookupInstance(ownerModule, entityType, entityId!)) ?? null;
    } catch {
      return null;
    }
  };

  const {
    data: instance,
    refetch,
    isFetched,
    isError,
  } = useQuery({
    queryKey: [
      'approvals',
      'lookup',
      essScope ? 'ess' : allowRequesterView ? 'requester' : 'hr',
      ownerModule,
      entityType,
      entityId,
    ],
    queryFn: lookupInstance,
    enabled,
    ...tierOptions('operational'),
  });

  const { data: workflowUi } = useWorkflowActions(ownerModule, entityType, entityId);

  const act = async (action: string, delegateToUserId?: number) => {
    if (!instance?.id) return;
    setActing(true);
    try {
      if (action === 'RESUBMIT') {
        await approvalsApi.resubmit(instance.id);
        notify.t.success('toast.resubmittedForApproval', { ns: 'workflow' });
      } else {
        const result = await approvalsApi.act(
          instance.id,
          action,
          comments || undefined,
          delegateToUserId,
        );
        notify.t.success('toast.requestStatus', { status: result.status.toLowerCase(), ns: 'workflow' });
      }
      setComments('');
      await refetch();
      invalidate.approvals(qc);
      onActionComplete?.(action);
    } catch (err) {
      showApiError(err, i18n.t('errors.approvalActionFailed', { ns: 'workflow' }));
    } finally {
      setActing(false);
    }
  };

  const normalizedEntityType = (entityType ?? '').toUpperCase();
  const isSubjectOwnerEntity = new Set(['LEAVE', 'ATTENDANCE', 'OVERTIME']).has(normalizedEntityType);
  const isRequester =
    isSubjectOwnerEntity
      ? isLeaveOwnerUser({
          currentUserId,
          currentEmployeeId,
          instance,
          subjectEmployeeId,
        })
      : currentUserId != null && instance?.requestedBy === currentUserId;
  const isReturnedApproval = isLeaveApprovalReturned(instance, resolvedRecordStatus);
  const canActOnInstance = instance?.canAct === true;
  const canViewInstance = instance?.canView === true;
  const canShow =
    isFetched &&
    !isError &&
    !!instance &&
    (canViewInstance ||
      canActOnInstance ||
      canUseApprovalLookup ||
      (allowRequesterView && isRequester) ||
      (isRequester && isReturnedApproval));

  return {
    instance,
    workflowUi,
    comments,
    setComments,
    acting,
    act,
    isFetched,
    isError,
    canShow,
    canActOnInstance,
    canViewInstance,
    canUseApprovalLookup,
    currentUserId,
    isRequester,
    refetch,
  };
}
